import _ from 'lodash';
import { isObject, isString } from 'lodash';

interface HasPropClasses extends Function {
  propClasses?: { [key: string]: string | { [key: string]: string } };
}

interface InstanceOfComponentWithPropClasses<Props, State> extends React.Component<Props, State> {
  props: Props;
  constructor: HasPropClasses;
}

/* Helper function to connect props with classnames
 *
 * const props = {
 *   color: 'green',
 * }
 *
 * const propsToClasses = {
 *   color: {
 *     green: 'green-class',
 *     red: 'red-class',
 *   }
 * }
 *
 * <div classNames={getPropClassesFromInstance(this)} />
 */
export function getPropClassesFromInstance(
  componentInstance: InstanceOfComponentWithPropClasses<any, any>,
  filteredProps?: string[]
): string[] {
  return getPropClasses(
    componentInstance.props,
    // $FlowFixMe property `propClasses` of unknown type
    componentInstance.constructor.propClasses || {},
    filteredProps
  );
}

/* If you don't have a component instance, like in a stateless function
 * want you can use getPropClasses directly.
 *
 * const props = {
 *   color: 'green',
 * }
 *
 * const propsToClasses = {
 *   color: {
 *     green: 'green-class',
 *     red: 'red-class',
 *   }
 * }
 *
 * <div classNames={getPropClasses(props, propsToClasses)} />
 */

export default function getPropClasses(
  props: { [key: string]: any },
  propsToClasses: { [key: string]: string | { [key: string]: string } },
  filteredProps?: string[]
): string[] {
  const whiteListedProps = filteredProps || Object.keys(propsToClasses);

  const propBasedClasses = whiteListedProps.reduce((classNames: string[], prop: string) => {
    const propVal = props[prop];
    const propToClassesVal = propsToClasses[prop];
    if (propVal) {
      if (propVal === true && isString(propToClassesVal)) {
        classNames.push(propToClassesVal);
      } else if (isObject(propToClassesVal) && propVal in propToClassesVal) {
        classNames.push(propToClassesVal[propVal]);
      }
    }

    return classNames;
  }, []);

  // some propToClasses map to null values
  return _.compact(propBasedClasses);
}
